import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

export const EASE = [0.16, 1, 0.3, 1];

export const Lightbox = ({ gallery, onClose, closeLabel, prevLabel, nextLabel }) => {
  const items = gallery?.items ?? [];
  const [index, setIndex] = useState(gallery?.index ?? 0);

  useEffect(() => {
    setIndex(gallery?.index ?? 0);
  }, [gallery]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (items.length > 1) {
        if (e.key === "ArrowRight") setIndex((i) => (i + 1) % items.length);
        if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + items.length) % items.length);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, items.length]);

  useEffect(() => {
    if (gallery) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
    }
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [gallery]);

  const image = items[index];

  return (
    <AnimatePresence>
      {image && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-0 z-[200] bg-ink/90 flex items-center justify-center p-5 md:p-12"
          onClick={onClose}
          data-testid="lightbox-overlay"
        >
          {items.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                aria-label={prevLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex((i) => (i - 1 + items.length) % items.length);
                }}
                className="absolute left-3 md:left-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 border border-paper/40 text-paper font-mono text-lg flex items-center justify-center transition-colors duration-300 hover:bg-paper hover:text-ink"
              >
                ←
              </button>
              <button
                data-testid="lightbox-next"
                aria-label={nextLabel}
                onClick={(e) => {
                  e.stopPropagation();
                  setIndex((i) => (i + 1) % items.length);
                }}
                className="absolute right-3 md:right-10 top-1/2 -translate-y-1/2 z-10 w-11 h-11 border border-paper/40 text-paper font-mono text-lg flex items-center justify-center transition-colors duration-300 hover:bg-paper hover:text-ink"
              >
                →
              </button>
            </>
          )}
          <motion.figure
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="max-w-[94vw] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={image.src}
                src={image.src}
                alt={image.label}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="max-w-full max-h-[74vh] object-contain border border-paper/30 bg-ink"
              />
            </AnimatePresence>
            <figcaption className="mt-4 flex items-center justify-between gap-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper/70">{image.label}</span>
              <div className="flex items-center gap-5 shrink-0">
                {items.length > 1 && (
                  <span className="font-mono text-[10px] tracking-[0.25em] text-paper/50" data-testid="lightbox-counter">
                    {index + 1} / {items.length}
                  </span>
                )}
                <button
                  data-testid="lightbox-close"
                  onClick={onClose}
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-paper border border-paper/40 px-4 py-2 transition-colors duration-300 hover:bg-paper hover:text-ink"
                >
                  {closeLabel}
                </button>
              </div>
            </figcaption>
          </motion.figure>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const scrollToId = (id) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(id, { offset: -64, duration: 1.5 });
  } else {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  }
};

export const Reveal = ({ children, delay = 0, className = "", y = 28 }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
    className={className}
  >
    {children}
  </motion.div>
);

export const Chapter = ({ num, label, dark = false }) => (
  <div className="flex items-center gap-4" data-testid={`chapter-${label?.toLowerCase().replace(/\s+/g, "-")}`}>
    <span className={`font-mono text-[11px] tracking-[0.25em] ${dark ? "text-proof" : "text-proof"}`}>{num}</span>
    <span className={`h-px w-8 ${dark ? "bg-paper/30" : "bg-ink/30"}`} />
    <span className={`font-mono text-[11px] uppercase tracking-[0.25em] ${dark ? "text-paper/60" : "text-smoke"}`}>{label}</span>
  </div>
);

// Platzhalter-Rahmen im Stil eines leeren InDesign-Bildrahmens ("Druckplatte").
// Sobald ein Foto vorliegt: src in content.js setzen — der Rahmen rendert dann das Bild.
export const ImagePlaceholder = ({ label, awaiting, src, pos, ratio = "aspect-[4/3]", className = "", testid }) => (
  <div
    data-testid={testid || `plate-${label?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className={`relative border border-ink/25 bg-cardboard overflow-hidden ${ratio} ${className} group`}
  >
    {src ? (
      <>
        <img
          src={src}
          alt={label}
          loading="lazy"
          style={pos ? { objectPosition: pos } : undefined}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03]"
        />
        <CropMarks className="text-paper/70 z-10" />
        <span className="absolute top-3 right-3 z-10 bg-paper/95 w-6 h-6 flex items-center justify-center font-mono text-xs text-ink opacity-0 group-hover:opacity-100 transition-opacity duration-300" aria-hidden="true">
          +
        </span>
        <span className="absolute bottom-3 left-3 z-10 bg-paper/95 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-ink">
          {label}
        </span>
      </>
    ) : (
      <>
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" aria-hidden="true">
          <line x1="0" y1="0" x2="100%" y2="100%" stroke="rgba(34,34,32,0.12)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
          <line x1="100%" y1="0" x2="0" y2="100%" stroke="rgba(34,34,32,0.12)" strokeWidth="1" vectorEffect="non-scaling-stroke" />
        </svg>
        <CropMarks className="text-ink/40" />
        <div className="relative z-10 flex flex-col items-center gap-2 px-4 text-center">
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-proof">{awaiting}</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-smoke leading-relaxed">{label}</span>
        </div>
      </>
    )}
  </div>
);

export const CropMarks = ({ className = "text-ink/50" }) => (
  <>
    <span className={`absolute top-2 left-2 w-3 h-3 border-t border-l border-current ${className}`} aria-hidden="true" />
    <span className={`absolute top-2 right-2 w-3 h-3 border-t border-r border-current ${className}`} aria-hidden="true" />
    <span className={`absolute bottom-2 left-2 w-3 h-3 border-b border-l border-current ${className}`} aria-hidden="true" />
    <span className={`absolute bottom-2 right-2 w-3 h-3 border-b border-r border-current ${className}`} aria-hidden="true" />
  </>
);

export const RegTarget = ({ className = "" }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1">
    <circle cx="20" cy="20" r="12" />
    <circle cx="20" cy="20" r="4" />
    <line x1="20" y1="0" x2="20" y2="40" />
    <line x1="0" y1="20" x2="40" y2="20" />
  </svg>
);

export const Noise = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none fixed inset-0 z-[90] opacity-[0.035] mix-blend-multiply"
    style={{
      backgroundImage:
        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
    }}
  />
);
